/**
 * Error Message Localization Module
 *
 * Provides centralized error code to i18n key mapping and localized error messages.
 * This module is the single source of truth for error message localization across the app.
 *
 * @module i18n/error-localization
 * @see SPEC-017 Requirement 9: Error Message Localization
 */

import { ApiErrorCode, type ApiError } from '@linkode/shared';

// ============================================================================
// Types
// ============================================================================

/**
 * Extended ApiError with i18n key for localization
 */
export interface LocalizedApiError extends ApiError {
  /** The i18n translation key for this error */
  i18nKey: string;
}

/**
 * Translation function type (compatible with i18next t function)
 */
export type TranslationFunction = (key: string, params?: Record<string, unknown>) => string;

/**
 * Options for getLocalizedErrorMessage
 */
export interface LocalizedErrorOptions {
  /** Override the default i18n key with a custom interpolation key */
  interpolationKey?: string;
  /** Parameters for interpolation */
  params?: Record<string, unknown>;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Comprehensive i18n key mapping for all API error codes
 * Maps ApiErrorCode to translation keys for user-friendly messages
 *
 * @see Requirement 9.2: Map error codes to localized messages
 */
export const ERROR_CODE_I18N_MAP: Record<ApiErrorCode, string> = {
  // Auth errors (401, 403)
  [ApiErrorCode.UNAUTHORIZED]: 'errors.unauthorized',
  [ApiErrorCode.FORBIDDEN]: 'errors.forbidden',

  // Resource errors (404)
  [ApiErrorCode.DOC_NOT_FOUND]: 'errors.docNotFound',
  [ApiErrorCode.NOT_FOUND]: 'errors.notFound',

  // Validation errors (400)
  [ApiErrorCode.VALIDATION_ERROR]: 'errors.validationError',
  [ApiErrorCode.BAD_REQUEST]: 'errors.badRequest',

  // Rate limiting (429)
  [ApiErrorCode.RATE_LIMITED]: 'errors.rateLimited',

  // Infrastructure errors (503)
  [ApiErrorCode.UPSTASH_UNAVAILABLE]: 'errors.upstashUnavailable',
  [ApiErrorCode.EMAIL_SERVICE_UNAVAILABLE]: 'errors.emailServiceUnavailable',

  // Generic errors
  [ApiErrorCode.INTERNAL_ERROR]: 'errors.internalError',
  [ApiErrorCode.VERSION_CONFLICT]: 'errors.versionConflict',

  // Folder errors (SPEC-016)
  [ApiErrorCode.FOLDER_NOT_FOUND]: 'errors.folderNotFound',
  [ApiErrorCode.FOLDER_ALREADY_EXISTS]: 'errors.folderAlreadyExists',
  [ApiErrorCode.FOLDER_DEPTH_EXCEEDED]: 'errors.folderDepthExceeded',
  [ApiErrorCode.INVALID_FOLDER_NAME]: 'errors.invalidFolderName',

  // Document errors (SPEC-016)
  [ApiErrorCode.DOC_ALREADY_DELETED]: 'errors.docAlreadyDeleted',

  // AI Apply errors (SPEC-014)
  [ApiErrorCode.AI_APPLY_VALIDATION_ERROR]: 'errors.aiApplyValidationError',
  [ApiErrorCode.AI_APPLY_FRONTMATTER_PARSE_ERROR]: 'errors.aiApplyFrontmatterParseError',
  [ApiErrorCode.AI_APPLY_FAILED]: 'errors.aiApplyFailed',
};

// ============================================================================
// Error Classification
// ============================================================================

/**
 * Check if an error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    'requestId' in error
  );
}

/**
 * Check if an error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }
  if (error instanceof Error && error.name === 'NetworkError') {
    return true;
  }
  return false;
}

/**
 * Check if the browser is offline
 */
export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && !navigator.onLine;
}

// ============================================================================
// Error i18n Key Resolution
// ============================================================================

/**
 * Get the i18n key for an error
 *
 * Priority:
 * 1. Offline state -> 'errors.offline'
 * 2. Network errors -> 'errors.networkError'
 * 3. API errors -> mapped from ERROR_CODE_I18N_MAP
 * 4. Unknown errors -> 'errors.unknown'
 *
 * @param error - The error to get the i18n key for
 * @returns The i18n translation key
 *
 * @see Requirement 9.2: Map error codes to localized messages
 * @see Requirement 9.4: Display generic error for missing messages
 */
export function getErrorI18nKey(error: unknown): string {
  // Check for offline state first
  if (isOffline()) {
    return 'errors.offline';
  }

  // Check for network errors
  if (isNetworkError(error)) {
    return 'errors.networkError';
  }

  // Check for API errors
  if (isApiError(error)) {
    return ERROR_CODE_I18N_MAP[error.code] ?? 'errors.unknown';
  }

  // Default to unknown error
  return 'errors.unknown';
}

// ============================================================================
// Localized Error Messages
// ============================================================================

/**
 * Get a localized error message using the provided translation function
 *
 * @param error - The error to get the message for
 * @param t - Translation function (i18next t function)
 * @param options - Optional interpolation options
 * @returns Localized error message
 *
 * @see Requirement 9.1: Display error in user's language
 * @see Requirement 9.3: Support error message interpolation
 *
 * @example
 * ```typescript
 * const { t } = useTranslation();
 * const message = getLocalizedErrorMessage(error, t);
 *
 * // With interpolation
 * const message = getLocalizedErrorMessage(error, t, {
 *   interpolationKey: 'errors.fileNotFound',
 *   params: { filename: 'document.md' }
 * });
 * ```
 */
export function getLocalizedErrorMessage(
  error: unknown,
  t: TranslationFunction,
  options?: LocalizedErrorOptions
): string {
  // Use custom interpolation key if provided
  if (options?.interpolationKey) {
    return t(options.interpolationKey, options.params);
  }

  // Get the i18n key for the error
  const i18nKey = getErrorI18nKey(error);

  // Return translated message
  return t(i18nKey, options?.params);
}

// ============================================================================
// Error Factory
// ============================================================================

/**
 * Create a LocalizedApiError with the i18n key pre-populated
 *
 * @param code - Error code from ApiErrorCode enum
 * @param message - Human-readable error message (fallback)
 * @param requestId - Request tracking ID
 * @param details - Optional additional error details
 * @returns LocalizedApiError with i18nKey
 *
 * @see Requirement 9.5: Log original error code for debugging
 */
export function createLocalizedError(
  code: ApiErrorCode,
  message: string,
  requestId: string,
  details?: Record<string, unknown>
): LocalizedApiError {
  return {
    code,
    message,
    requestId,
    details,
    i18nKey: ERROR_CODE_I18N_MAP[code] ?? 'errors.unknown',
  };
}

// ============================================================================
// Export Index
// ============================================================================

export const errorLocalization = {
  ERROR_CODE_I18N_MAP,
  isApiError,
  isNetworkError,
  isOffline,
  getErrorI18nKey,
  getLocalizedErrorMessage,
  createLocalizedError,
};
